import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { TableCell, TableRow } from '@/ui/base/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/base/tooltip';
import { formatarDinheiro } from '@/nucleo/dinheiro';

import type { OrdemServicoAteste } from '../tipos';
import { DrillDias } from './DrillDias';

/**
 * Nível 3 do drill-down: OSs de um credenciado.
 *
 * Renderiza linhas dentro da MESMA grid de 10 colunas do nível 2
 * (`CredenciadosTable`), para o alinhamento visual ficar correto com o
 * cabeçalho do credenciado — por isso devolve `TableRow`s soltas, não uma
 * subtabela.
 */
export function DrillOSsRows({ ordensServico }: { ordensServico: OrdemServicoAteste[] }) {
  const [osAberta, setOsAberta] = useState<string | null>(null);

  if (ordensServico.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={10} className="bg-muted/20 py-3 pl-16 text-xs text-muted-foreground">
          Nenhuma OS com apontamentos no mês.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {ordensServico.map((os) => {
        const aberta = osAberta === os.ordemServicoId;
        return (
          <Fragment key={os.ordemServicoId}>
            <TableRow className="border-l-2 border-primary/30 bg-muted/20 text-xs hover:bg-muted/30">
              <TableCell className="w-10 pl-6">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setOsAberta(aberta ? null : os.ordemServicoId)}
                  aria-label="Expandir dias"
                >
                  {aberta ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </Button>
              </TableCell>
              <TableCell className="font-mono text-muted-foreground">OS #{os.numero}</TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-[10px]">
                  {os.tipoTeg}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
              <TableCell className="text-right">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 tabular-nums text-xs">
                      <span className="text-muted-foreground">{os.qtdDias} d</span>
                      <span className="text-muted-foreground/80">·</span>
                      <span>{os.totalAlunosNaoCad} NC</span>
                      <span className="text-muted-foreground/80">·</span>
                      <span>{os.totalAlunosCad} C</span>
                      <span className="text-muted-foreground/80">·</span>
                      <span className="text-muted-foreground">{os.totalKm} km</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-0.5 text-xs">
                      <div>
                        <b>{os.qtdDias}</b> dia(s) apontado(s)
                      </div>
                      <div>
                        <b>{os.totalAlunosNaoCad}</b> alunos não cadeirantes
                      </div>
                      <div>
                        <b>{os.totalAlunosCad}</b> alunos cadeirantes
                      </div>
                      <div>
                        <b>{os.totalKm}</b> km adicionais
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TableCell>
              <TableCell className="text-right font-mono">{formatarDinheiro(os.valor)}</TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
            </TableRow>
            {aberta && (
              <TableRow className="border-l-2 border-primary/30">
                <TableCell colSpan={10} className="border-0 bg-muted/10 p-0">
                  <DrillDias dias={os.dias} />
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        );
      })}
    </>
  );
}
